import { columns } from "@/columns";
import {
  AddPartnerModal,
  DeleteConfirmModal,
  DynamicTable,
  IconImage,
  PageHeader,
  RestrictUserModal,
  UnblockUserModal,
  UpdatePartnerModal,
} from "@/components";
import { black_filter_img, search_img } from "@/constants";
import { useDebounce, useSearchQuery, useSnackbar } from "@/hooks";
import {
  useDeleteSpOwnerPartnerMutation,
  useGetAllSpOwnerPartnersQuery,
  usePostSpOwnerPartnerMutation,
  useUpdatePartnerStatusMutation,
  useUpdateSpOwnerPartnerMutation,
} from "@/services";
import { Box, InputAdornment, Typography } from "@mui/material";
import { MouseEvent, useState } from "react";
import { IoMdAdd } from "react-icons/io";
import Filters from "./filters";
import {
  HeaderRightNode,
  StyledContainedButton,
  StyledTextInput,
} from "./styles";
import { useNavigate } from "react-router-dom";
import { useAppSelector } from "@/store";
import { roles } from "@/roles";

const initialModalState = {
  type: "", // "delete", "restrict", "unblock"
  open: false,
  partnerName: "",
  _id: "",
  isRestricted: false,
  email: "",
};

const initalFilterState: Pick<UserFilter, "status"> = {
  status: "",
};

const SPOWNERPartners = () => {
  const { updateQuery, getQueryValue } = useSearchQuery();
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [filterState, setFilterState] = useState(initalFilterState);
  const [modalState, setModalState] = useState(initialModalState);
  const [showAddPartnerModal, setShowAddPartnerModal] = useState(false);
  const [showUpdatePartnerModal, setShowUpdatePartnerModal] = useState(false);
  const [deleteSpOwnerPartner, { isLoading: isDeletingUser }] =
    useDeleteSpOwnerPartnerMutation();
  const [postSpOwnerPartner, { isLoading: isCreatingPartner }] =
    usePostSpOwnerPartnerMutation();
  const [updateSpOwnerPartner, { isLoading: isUpdatingPartner }] =
    useUpdateSpOwnerPartnerMutation();

  const [updatePartnerStatus, { isLoading: partnerStatusLoading }] =
    useUpdatePartnerStatusMutation();

  const [anchorElement, setAnchorElement] = useState<HTMLButtonElement | null>(
    null
  );
  const [partnerDetails, setPartnerDetails] = useState<any>({
    firstName: "",
    lastName: "",
    email: "",
    _id: "",
    accessibleClients: [] as string[],
  });
  const filterCount = Object.values(filterState).filter(Boolean).length;
  const navigate = useNavigate();
  const { selectedTenant } = useAppSelector((state) => state.tenantManagement);
  const user = useAppSelector((state) => state.auth.user);
  const isTDAdminSP =
    user?.userRole === roles.TDADMIN &&
    selectedTenant?.tenantType === "SERVICE_PROVIDER";

  const {
    data: apiData,
    isLoading,
    isFetching,
  } = useGetAllSpOwnerPartnersQuery(
    {
      limit,
      page,
      searchKeyword: getQueryValue("search"),
      ...(filterState.status && {
        isRestricted: filterState.status !== "active" ? true : false,
      }),
      ...(isTDAdminSP && {
        tenantId: selectedTenant?._id,
      }),
    },
    {
      refetchOnMountOrArgChange: true,
    }
  );

  const { show } = useSnackbar();
  const { data = [], meta } = apiData || {};
  const { open, partnerName } = modalState;
  const getErrorMessage = (error: unknown): string => {
    if (error instanceof Error) {
      return error.message;
    }

    if (typeof error === "object" && error !== null && "message" in error) {
      return String((error as { message: unknown }).message);
    }

    return String(error);
  };

  const handleTriggerModal = (item: SPOwnerPartnerTypes, type: string) => {
    if (type === "redirect") {
      navigate(`/clients?partner=${item?._id}`);
    } else {
      setModalState({
        type,
        open: true,
        partnerName: item.firstName + " " + item.lastName,
        _id: item?._id,
        email: item?.email,
        isRestricted: item.isRestricted,
      });
    }
  };

  const handleFilterChange = async (e: Pick<UserFilter, "status">) => {
    setFilterState(e);
  };

  const handleCloseModal = () => {
    setModalState(initialModalState);
  };

  const debouncedSearch = useDebounce((val: string) => {
    updateQuery({ search: val });
  }, 500);

  const handleOpenFilterPopover = (event: MouseEvent<HTMLButtonElement>) => {
    setAnchorElement(event.currentTarget);
  };

  const handleRowClick = (partner: SPOwnerPartnerTypes) => {
    setPartnerDetails({
      firstName: partner.firstName,
      lastName: partner.lastName,
      email: partner.email,
      _id: partner._id,
      accessibleClients: (partner as any)?.accessibleClients || [],
      allowedPages: (partner as any)?.allowedPages || [],
    });
    setShowUpdatePartnerModal(true);
  };

  const handleDeletePartner = async () => {
    const userId = {
      userId: modalState?._id as string,
      ...(isTDAdminSP && { tenantId: selectedTenant?._id }),
    };

    try {
      await deleteSpOwnerPartner(userId).unwrap();
      handleCloseModal();
    } catch (error) {
      show(getErrorMessage(error), "", "error");
    }
  };

  const handleCreatePartner = async (data: SPOwnerPartnerTypes) => {
    const partnerData = {
      ...data,
      ...(isTDAdminSP && { tenantId: selectedTenant?._id }),
      ...(isTDAdminSP && {
        createdBy: selectedTenant?.users[0]._id
          ? selectedTenant?.users[0]._id
          : undefined,
      }),
    };
    try {
      await postSpOwnerPartner(partnerData).unwrap();
      setShowAddPartnerModal(false);
    } catch (error) {
      show(getErrorMessage(error), "", "error");
    }
  };

  const handleUpdatePartner = async (data: SPOwnerPartnerTypes) => {
    const { firstName, lastName } = data;
    try {
      await updateSpOwnerPartner({
        partnerData: { firstName, lastName },
        partnerId: partnerDetails?._id as string,
      }).unwrap();
      setShowUpdatePartnerModal(false);
    } catch (error) {
      show(getErrorMessage(error), "", "error");
    }
  };

  const handleUpdatePartnerStatus = async () => {
    try {
      await updatePartnerStatus({
        email: modalState.email,
        isRestricted: !modalState.isRestricted,
      }).unwrap();
      show(
        `Partner ${modalState.isRestricted ? "unblocked" : "restricted"}!`,
        "",
        "success"
      );
      setModalState(initialModalState);
    } catch (error: any) {
      show(error.message, "", "error");
    }
  };

  return (
    <>
      <PageHeader
        title={`Partners`}
        count={meta?.countDocs || 0}
        rightNode={
          <HeaderRightNode gap={2}>
            <StyledTextInput
              type="text"
              placeholder="Search"
              handleChangeValue={debouncedSearch}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <IconImage src={search_img} size="auto" />
                  </InputAdornment>
                ),
              }}
            />

            <Box sx={{ position: "relative" }}>
              {filterCount > 0 && (
                <Box
                  sx={{
                    width: 24,
                    height: 24,
                    display: "grid",
                    placeContent: "center",
                    backgroundColor: "error.main",
                    color: "white",
                    borderRadius: "50%",
                    position: "absolute",
                    top: "-10px",
                    right: "-9px",
                    zIndex: "1",
                  }}
                >
                  <Typography variant="caption" fontWeight={500} color="white">
                    {filterCount}
                  </Typography>
                </Box>
              )}

              <StyledContainedButton
                size="large"
                title="Filter"
                variant="outlined"
                style={{ borderColor: "#E6E6E9", height: "46px" }}
                startIcon={<IconImage src={black_filter_img} size="auto" />}
                onClick={handleOpenFilterPopover}
              />
            </Box>
            <StyledContainedButton
              startIcon={<IoMdAdd />}
              size="large"
              title="Add Partner"
              onClick={() => {
                setShowAddPartnerModal(true);
                setPartnerDetails({
                  firstName: "",
                  lastName: "",
                  email: "",
                  _id: "",
                });
              }}
            />
          </HeaderRightNode>
        }
      />
      <DynamicTable
        data={data as SPOwnerPartnerTypes[]}
        columns={columns({
          handleTriggerModal,
        })}
        isLoading={isLoading || isFetching}
        handlePageChange={setPage}
        handleLimitChange={(e) => {
          setPage(1);
          setLimit(e);
        }}
        limit={limit}
        page={page}
        total={meta?.countDocs || 0}
        hidePagination={false}
        onRowClick={handleRowClick}
        stickyHeader
        hideScrollBars
        isFullHeight
        sx={{
          flex: 1,
          "& .MuiTable-root": {
            borderCollapse: "collapse",
          },
          "& .MuiTableRow-root:last-of-type .MuiTableCell-root": {
            borderBottom: "1px solid #e6e6e9 !important",
          },
        }}
      />
      {/* Delete confirmation Modal */}
      {modalState.type === "delete" && (
        <DeleteConfirmModal
          open={open}
          itemName={`Partner “${partnerName}”`}
          subText="partner"
          onConfirm={handleDeletePartner}
          maxWidth="xs"
          onClose={handleCloseModal}
          btnLoader={isDeletingUser}
        />
      )}
      {/* Restrict User Modal */}
      {modalState.type === "restrict" && (
        <RestrictUserModal
          open={open}
          name={partnerName}
          onConfirm={handleUpdatePartnerStatus}
          onClose={handleCloseModal}
          loading={partnerStatusLoading}
        />
      )}
      <Filters
        anchorEl={anchorElement}
        onFiltersChange={handleFilterChange}
        initialFilterState={initalFilterState}
        onClose={() => setAnchorElement(null)}
      />

      {/* Unblock User Modal */}
      {modalState.type === "unblock" && (
        <UnblockUserModal
          open={open}
          name={partnerName}
          onConfirm={handleUpdatePartnerStatus}
          onClose={handleCloseModal}
          loading={partnerStatusLoading}
        />
      )}
      {/* Add Partner Modal */}
      {showAddPartnerModal && (
        <AddPartnerModal
          handleCreatePartner={handleCreatePartner}
          show={showAddPartnerModal}
          setShow={setShowAddPartnerModal}
          btnLoader={isCreatingPartner}
        />
      )}
      {/* Update Partner Modal */}
      {showUpdatePartnerModal && (
        <UpdatePartnerModal
          show={showUpdatePartnerModal}
          setShow={setShowUpdatePartnerModal}
          partnerDetails={partnerDetails}
          handleUpdatePartner={handleUpdatePartner}
          btnLoader={isUpdatingPartner}
        />
      )}
    </>
  );
};

export default SPOWNERPartners;
